<template>
    <div class="payment-summary" :class="countryClass">
        <div class="payment-summary__two-col upgrade-annual-toggle">
            <h4 class="payment-summary__header" data-ci="payment-summary__header">
                {{ pageAttr.billing_summary_title }}
            </h4>
            <a
                v-if="changeLinkEnabled"
                @click="handleChangeClicked"
                class="payment-summary__header-change">
                {{ pageAttr.billing_summary_change_plan_cta }}
            </a>
            <div v-if="!changeLinkEnabled"></div>
            <div
                data-testid="selectedPlan"
                data-ci="payment-summary__selectedPlan"
                class="payment-summary__row">
                {{ planLabel }}
            </div>
            <div
                data-testid="currentPrice"
                data-ci="payment-summary__currentPrice"
                class="payment-summary__row">
                {{ internationalizedPriceWithUnit }}
                <span v-if="featureAsterisk && !isExSubscriber">*</span>
            </div>

            <UpgradeAnnualToggle
                v-if="isUpgradeAnnualToggle && !isFTC"
                :plan-type="plan.planType"
                @filterAndSelect="togglePlanType"></UpgradeAnnualToggle>
        </div>

        <template v-if="isFTC">
            <a
                class="payment-summary__legal-overlay-open"
                @click="handleOpenModal"
                data-testid="legal-overlay-open"
                data-ci="payment-summary__legal-overlay-open">
                {{ pageAttr.aa_learn_more_copy_cta }}
            </a>
            <div
                v-if="trialString || isUpgradeAnnualToggle"
                class="payment-summary__section-border" />
            <div v-if="trialString" class="payment-summary__two-col">
                <div class="payment-summary__row">
                    {{ pageAttr.aa_order_summary_trial_label }}
                </div>
                <div class="payment-summary__row">
                    {{ trialString }}
                </div>
            </div>
            <UpgradeAnnualToggle
                v-if="isUpgradeAnnualToggle"
                :plan-type="plan.planType"
                @filterAndSelect="togglePlanType"></UpgradeAnnualToggle>
        </template>

        <template v-if="legalAttributesAreValid && !isFTC">
            <div class="payment-summary__section-border" />
            <div class="payment-summary__two-col">
                <div
                    class="payment-summary__terms-service"
                    data-testid="terms-service"
                    data-ci="payment-summary__terms-service">
                    {{ pageAttr.plan_info }}
                </div>
                <a
                    class="payment-summary__legal-overlay-open"
                    @click="handleOpenModal"
                    data-testid="legal-overlay-open"
                    data-ci="payment-summary__legal-overlay-open">
                    {{ pageAttr.learn_more_copy }}
                </a>
            </div>
        </template>
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import {
        FTC_COMPLIANCE_SWITCHPLAN_ENABLED,
        PAYMENT_SUMMARY_CHANGE_LINK_ENABLED,
        PRICE_WITH_ASTERISK,
    } from 'helpers/featureConstants';
    import commonMixins from 'aa/vue/plugins/account/commonMixins';
    import sharedPurchaseMixin from 'aa/vue/plugins/account/sharedPurchaseMixin';
    import Product from 'aa/vue/models/Product';
    import UpgradeAnnualToggle from 'aa/vue/components/UpgradeAnnualToggle';
    import { ACTION_NAMES, PAGE_TYPES } from 'services/Tracking';
    import { catanAddOnCode } from 'aa/vue/constants/aaBundle';

    export default {
        name: 'BillingPlanSummary',

        components: {
            UpgradeAnnualToggle,
        },

        props: {
            internationalizedPriceWithUnit: {
                type: String,
            },
            coupon: {
                type: Object,
            },
            countryClass: {
                type: String,
            },
            pageAttr: {
                type: Object,
            },
            plan: {
                type: Object,
            },
            isFTC: {
                type: Boolean,
                default: false,
            },
            isFromDirect: {
                type: Boolean,
                default: false,
            },
        },

        data: function () {
            return {
                isPaymentPage: true,
                featureAsterisk: false,
                isUserOriginalPlanTypeMonthly: undefined,
                isMonthPlanTypeOnLoad: undefined,
            };
        },
        mixins: [commonMixins, sharedPurchaseMixin],

        methods: {
            handleChangeClicked() {
                if (this.isFromDirect) {
                    this.$store.dispatch('plan/updateSelectedPlanWithTypeandTier', {});
                }
                if (this.isFTC && !this.isSwitchPlan) {
                    var baseLink = '/account/signup/pickplan/';
                    var bundleLink = `${baseLink}bundle/${catanAddOnCode().toLowerCase()}/`;
                    this.trackingService.trackAction(ACTION_NAMES.TRACK_CHANGE_YOUR_PLAN, {
                        productOfferPeriod: this.plan?.trialString,
                        pageUrl: window.location.href,
                        pageType: 'svod_payment',
                        pickPlanType: this.planLabel,
                    });
                    const routerLink = this.$store.getters['isPartnerBundle']
                        ? bundleLink
                        : baseLink;
                    window.location.href = routerLink;
                    return;
                }
                if (this.isSwitchPlan) {
                    this.trackingService.trackAction(ACTION_NAMES.TRACK_CHANGE_YOUR_PLAN, {
                        pageType: PAGE_TYPES.PLAN_CHANGE_CONFIRMATION,
                        ctaText: this.pageAttr.billing_summary_change_plan_cta,
                        screenName: '/switch-plan/',
                        productCurrentSku: this.plan.recurlyCode,
                        pickPlanType: this.planLabel,
                        switchPlanClick: '1',
                    });
                    if (this.isFTC) {
                        this.$router.push({
                            name: 'AccountSwitchPlan',
                            params: { plan: this.plan.planType },
                        });
                        return;
                    }
                    this.$router.push({ name: 'AccountTier' });
                } else {
                    this.trackingService.trackAction(ACTION_NAMES.TRACK_CHANGE_YOUR_PLAN, {
                        pageUrl: window.location.href,
                        pageType: this.commonFlowPageType,
                        pickPlanType: this.planLabel,
                    });
                    this.$router.push({ name: 'tier' });
                }
            },
            handleOpenModal() {
                if (!this.legalAttributesAreValid && !this.isFTC) {
                    return;
                }

                this.openModal('BillingPlanLegalOverlayModal', this.selectedLegalAttributes);
                let pageType = this.getPageType(this.$route.params.directLinkFlow);
                this.trackingService.trackAction(ACTION_NAMES.LEARN_MORE_CTA, {
                    pageUrl: window.location.href,
                    pageType,
                });
            },
            getPageType(directLinkFlow = null) {
                return directLinkFlow
                    ? `winback_${directLinkFlow}_directLink_payment`
                    : this.commonFlowPageType;
            },
        },

        computed: {
            ...mapGetters(['featureIsActive']),
            ...mapGetters({
                selectedPlan: 'plan/getSelectedPlan',
                upgradeAnnualToggle: 'featureFlags/upgradeAnnualToggle',
            }),
            ...mapState('user', {
                isExSubscriber: (state) => state.isExSubscriber,
            }),
            classList() {
                return [this.countryClass, 'upgrade-annual-toggle'];
            },
            trialString() {
                if (
                    this.isFTC &&
                    this.isSwitchPlan &&
                    this.featureIsActive(FTC_COMPLIANCE_SWITCHPLAN_ENABLED)
                ) {
                    // US doesn't set `trial` to `false` when they don't want the trial plan
                    return '';
                }
                return this.coupon?.trialString || this.plan?.trialString;
            },
            selectedLegalAttributes() {
                if (this.isFTC && this.plan) {
                    const planTier =
                        this.plan.tier === Product.TIER_ESSENTIAL_NUMBER ? 'essential' : 'premium';
                    const planType =
                        this.plan.planType === Product.TYPE_MONTHLY ? 'monthly' : 'annual';
                    const copyKey =
                        planTier === 'essential'
                            ? `aa_learn_more_expanded_essential_${planType}`
                            : `aa_learn_more_expanded_paramount_premium_${planType}`;
                    return {
                        legal_copy_header: this.pageAttr.legal_copy_header,
                        chosen_legal_copy_desktop: this.pageAttr[copyKey],
                    };
                }
                return this.$store.getters['plan/getLegalOverlayAttributes'];
            },
            legalAttributesAreValid() {
                if (
                    !this.selectedLegalAttributes ||
                    !this.selectedLegalAttributes.legal_copy_header ||
                    !this.selectedLegalAttributes.chosen_legal_copy_desktop
                ) {
                    return false;
                }
                return true;
            },
            isSwitchPlan() {
                if (this.isFromDirect) {
                    return (
                        (this.$route.name === 'DIRECTLINK' ||
                            this.$route.name === 'directAccountSummary') &&
                        this.$route.path.includes('/sp/')
                    );
                }
                return this.$route.name !== 'PAYMENT_SUMMARY';
            },
            planLabel() {
                const selectedPlan = this.$store.getters['plan/getSelectedPlan'];

                if (this.isFTC) {
                    return selectedPlan?.planTitle;
                }
                const planLabel = this.$store.getters['plan/getSelectedPlanLabelKey'];
                return selectedPlan?.localizedPlanTitle ?? this.$getLocale(planLabel);
            },
            changeLinkEnabled() {
                return (
                    (this.isFTC || this.featureIsActive(PAYMENT_SUMMARY_CHANGE_LINK_ENABLED)) &&
                    this.$store.getters['plan/getPlans'].length > 1 &&
                    !this.$store.getters['flow/isEdu']
                );
            },
            getSignupRouterLink() {
                return '/account/signup/pickplan/';
            },
            isUpgradeAnnualToggle() {
                return (
                    this.upgradeAnnualToggle &&
                    !this.isSwitchPlan &&
                    this.isUserOriginalPlanTypeMonthly &&
                    !this.pageAttr?.aa_payment_upgrade_to_annual_hide &&
                    (this.canToggleFromMonthlyToAnnual || this.canToggleFromAnnualToMonthly)
                );
            },
        },
        created() {
            this.featureAsterisk = this.featureIsActive(PRICE_WITH_ASTERISK);
            this.isUserOriginalPlanTypeMonthly =
                this.upgradeAnnualToggle && this.plan?.planType === Product.TYPE_MONTHLY;
        },
    };
</script>
